import { useCallback, useState } from 'react';
import { Card } from '@amzn/stencil-react-components/card';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H3 } from '@amzn/stencil-react-components/text';
import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button';
// standards-ignore: cross-feature — IconPlusSmall is a generic "add" affordance; a shared wrapper over a bare icon would add indirection without value
import { IconPlusSmall } from '@amzn/stencil-react-icons';

import { InfoTooltip } from '../../../shared/ui/forms/InfoTooltip';
import { VerticalDivider } from '../../../shared/ui/VerticalDivider';
import { DocLinksField, type DocLinkItem } from '../../../shared/ui/forms/DocLinksField';
import { AddDocLinkModal } from './AddDocLinkModal';
import { useMessages } from '../../../shared/i18n/useMessages';
import {
  CONFIG_BUNDLE,
  type ConfigKey,
  dtProps,
  nativeDtProps,
  type QuestionConfigCardProps,
} from './questionConfigShared';

/**
 * The "Documentation" card of the Question Configuration step — the supporting
 * SOP / reference links that back the audit question. Shows the links in the
 * shared `DocLinksField` table and adds new ones through the "Add Document
 * Link" modal (V1 parity). Fully controlled via `patchConfig`.
 */
export function QuestionConfigDocumentation({
  config,
  patchConfig,
  disabled,
  dataTestId,
}: QuestionConfigCardProps): JSX.Element {
  const { t } = useMessages<ConfigKey>(CONFIG_BUNDLE);
  const infoAltText = t('configInfoAltText');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const handleAddLink = useCallback(
    (link: DocLinkItem) => {
      patchConfig({ docLinks: [...config.docLinks, link] });
    },
    [config.docLinks, patchConfig],
  );
  const handleRemoveLink = useCallback(
    (next: DocLinkItem[]) => {
      patchConfig({ docLinks: next });
    },
    [patchConfig],
  );

  return (
    <Card padding="dimensions.spacing.300" {...dtProps(dataTestId, 'doc-section')}>
      <Col gridGap="dimensions.spacing.300" width="100%">
        {/* Section header: title + Info on the left, the "Add Document Link"
         *  trigger on the right. The button opens the add-link modal. */}
        <Row
          alignItems="center"
          justifyContent="space-between"
          gridGap="dimensions.spacing.200"
          flexWrap="wrap"
        >
          <Row alignItems="center" gridGap="dimensions.spacing.200">
            <H3>{t('configDocumentationHeading')}</H3>
            <VerticalDivider />
            <InfoTooltip
              text={t('configDocumentationInfo')}
              ariaLabel={infoAltText}
              {...dtProps(dataTestId, 'doc-info')}
            />
          </Row>
          <Button
            size={ButtonSize.Small}
            variant={ButtonVariant.Secondary}
            icon={<IconPlusSmall aria-hidden="true" />}
            disabled={disabled}
            onClick={openModal}
            {...nativeDtProps(dataTestId, 'doc-add')}
          >
            {t('configDocAddButton')}
          </Button>
        </Row>
        <DocLinksField
          value={config.docLinks}
          onChange={handleRemoveLink}
          disabled={disabled}
          labels={{
            documentColumnHeader: t('configDocDocumentColumn'),
            linkColumnHeader: t('configDocLinkColumn'),
            actionsColumnHeader: t('configDocActionsColumn'),
            removeAltText: t('configDocRemoveAltText'),
            emptyText: t('configDocEmptyText'),
            sourceSopBadge: t('configDocSourceSopBadge'),
            sourceSopLockedReason: t('configDocSourceSopLockedTooltip'),
          }}
          {...dtProps(dataTestId, 'doc-links')}
        />
      </Col>
      <AddDocLinkModal
        isOpen={isModalOpen}
        existingLinks={config.docLinks}
        onAdd={handleAddLink}
        onClose={closeModal}
        labels={{
          title: t('configDocModalTitle'),
          documentLabel: t('configDocDocumentColumn'),
          documentPlaceholder: t('configDocTitlePlaceholder'),
          urlLabel: t('configDocLinkColumn'),
          urlPlaceholder: t('configDocUrlPlaceholder'),
          addButton: t('configDocAddButton'),
          cancelButton: t('configDocCancelButton'),
          invalidUrlText: t('configDocInvalidUrl'),
          duplicateUrlText: t('configDocDuplicateUrl'),
        }}
        {...dtProps(dataTestId, 'doc-modal')}
      />
    </Card>
  );
}
